<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { fromUnixTime, subDays, format } from 'date-fns';
import { OnClickOutside } from '@vueuse/components';
import { getUnixStartOfDay, getUnixEndOfDay } from 'shared/helpers/DateHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { REPORTS_PERMISSIONS } from 'dashboard/constants/permissions';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import BasePaywallModal from 'dashboard/routes/dashboard/settings/components/BasePaywallModal.vue';
import ReportHeader from 'dashboard/routes/dashboard/settings/reports/components/ReportHeader.vue';
import kanbanModule from 'kanban/store/modules/kanban';
import {
  REPORT_TABS,
  REPORT_TAB_ORDER,
  ALL_BOARDS_VALUE,
  ALL_AGENTS_VALUE,
  ALL_INBOXES_VALUE,
  EXPORTABLE_REPORTS,
  DEFAULT_TIMESERIES_METRIC,
  DEFAULT_GROUP_BY,
  DEFAULT_CFD_GROUP_BY,
  DEFAULT_RANGE_DAYS,
  BUILT_IN_DIMENSIONS,
  DEFAULT_DIMENSION,
  CUSTOM_ATTRIBUTE_DIMENSION,
  DIMENSION_DATE_GROUPS,
  DEFAULT_DIMENSION_DATE_GROUP,
  TASK_LIST_EXPORT,
} from 'kanban/constants/reports';
import {
  buildAttributeDimensionOptions,
  decodeDimension,
  dimensionRequestParams,
  encodeDimension,
} from 'kanban/helper/reportDimensions';
import {
  downloadBlob,
  fileNameFromDisposition,
  readJsonBlob,
} from 'kanban/helper/download';
import {
  buildReportParams,
  filtersFromRouteQuery,
} from 'kanban/helper/reportFilters';
import { useKanbanReports } from 'kanban/composables/useKanbanReports';
import { useKanbanFilterOptions } from 'kanban/composables/useKanbanFilterOptions';
import ReportsAPI from 'kanban/api/reports';
import KanbanSummaryCards from 'kanban/components/reports/KanbanSummaryCards.vue';
import KanbanThroughputChart from 'kanban/components/reports/KanbanThroughputChart.vue';
import KanbanAgentLeaderboard from 'kanban/components/reports/KanbanAgentLeaderboard.vue';
import KanbanFunnelChart from 'kanban/components/reports/KanbanFunnelChart.vue';
import KanbanCfdChart from 'kanban/components/reports/KanbanCfdChart.vue';
import KanbanAgingTable from 'kanban/components/reports/KanbanAgingTable.vue';
import KanbanStageDurations from 'kanban/components/reports/KanbanStageDurations.vue';
import KanbanBoardRequiredState from 'kanban/components/reports/KanbanBoardRequiredState.vue';
import KanbanDimensionTable from 'kanban/components/reports/KanbanDimensionTable.vue';
import KanbanDimensionCharts from 'kanban/components/reports/KanbanDimensionCharts.vue';
import KanbanTaskExportDialog from 'kanban/components/reports/KanbanTaskExportDialog.vue';
import TaskAttributeFiltersPanel from 'kanban/components/filters/TaskAttributeFiltersPanel.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();
const { isCloudFeatureEnabled, accountId, isOnChatwootCloud } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');
const attributesByModel = useMapGetter('attributes/getAttributesByModel');
const kanbanProducts = useMapGetter('kanban/getProducts');
const taskAttributeDefinitions = computed(() =>
  attributesByModel.value('task_attribute')
);
const { checkPermissions } = usePolicy();
// A lista de cartoes leva dados pessoais dos contatos: so administrador ou quem gerencia relatorios (o servidor
// confere de novo em ReportPolicy#export_tasks?).
const canExportTasks = computed(() =>
  checkPermissions(['administrator', REPORTS_PERMISSIONS])
);

// Paywall guard, mirroring KanbanOverviewPage (the route is already KANBAN-gated, this
// only covers the inactive-subscription edge case).
const fazerAiSubscription = computed(
  () => store.getters['globalConfig/getFazerAiSubscription']
);
const isFazerAiSubscriptionActive = computed(() =>
  ['active', 'past_due', 'trialing'].includes(fazerAiSubscription.value?.status)
);
const isKanbanEnabled = computed(
  () =>
    isCloudFeatureEnabled(FEATURE_FLAGS.KANBAN) &&
    isFazerAiSubscriptionActive.value
);
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const paywallI18nKey = computed(() =>
  isOnChatwootCloud.value ? 'PAYWALL' : 'ENTERPRISE_PAYWALL'
);
const openBilling = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};

if (!store.hasModule('kanban')) {
  store.registerModule('kanban', kanbanModule);
}
const boards = computed(() => store.state.kanban.boards);

// Filters (board + agent + period), seeded from the URL for refresh/share.
const selectedBoardId = ref(
  route.query.board_id ? String(route.query.board_id) : ALL_BOARDS_VALUE
);
const selectedAgentId = ref(
  route.query.agent_id ? String(route.query.agent_id) : ALL_AGENTS_VALUE
);
const selectedInboxId = ref(
  route.query.inbox_id ? String(route.query.inbox_id) : ALL_INBOXES_VALUE
);
const activeTab = ref(
  REPORT_TAB_ORDER.includes(route.query.tab)
    ? route.query.tab
    : REPORT_TABS.OVERVIEW
);
const rangeType = ref(route.query.range_type || DATE_RANGE_TYPES.LAST_30_DAYS);
// Only honor query timestamps that parse to finite numbers so a malformed since/until in the
// URL falls back to the default window instead of yielding an Invalid Date.
const dateFromQuery = (value, fallback) => {
  const timestamp = Number(value);
  return value && Number.isFinite(timestamp)
    ? fromUnixTime(timestamp)
    : fallback;
};
const dateRange = ref([
  dateFromQuery(route.query.since, subDays(new Date(), DEFAULT_RANGE_DAYS - 1)),
  dateFromQuery(route.query.until, new Date()),
]);

// Etiqueta, origem, produto e condicoes de atributo: mesmo motor e mesmos parametros do quadro do funil. A URL e
// lida sem validar as condicoes; initialize descarta as que nao valem depois de carregar os atributos.
const reportFilters = reactive(filtersFromRouteQuery(route.query).filters);
const showAttributeFilters = ref(false);
const reportFilterParams = computed(() => buildReportParams(reportFilters));

const metric = ref(DEFAULT_TIMESERIES_METRIC);
const groupBy = ref(DEFAULT_GROUP_BY);
const cfdGroupBy = ref(DEFAULT_CFD_GROUP_BY);
const agingPage = ref(1);
// Um atributo vem na URL em tres parametros (dimension, attribute_model, attribute_key); na tela e um valor so.
const selectedDimension = ref(
  route.query.dimension === CUSTOM_ATTRIBUTE_DIMENSION
    ? encodeDimension({
        attributeModel: route.query.attribute_model,
        attributeKey: route.query.attribute_key,
      })
    : route.query.dimension || DEFAULT_DIMENSION
);
const dimensionDateGroup = ref(
  DIMENSION_DATE_GROUPS.includes(route.query.date_group_by)
    ? route.query.date_group_by
    : DEFAULT_DIMENSION_DATE_GROUP
);

const since = computed(() => getUnixStartOfDay(dateRange.value[0]));
const until = computed(() => getUnixEndOfDay(dateRange.value[1]));

const boardId = computed(() =>
  selectedBoardId.value === ALL_BOARDS_VALUE
    ? null
    : Number(selectedBoardId.value)
);
const agentId = computed(() =>
  selectedAgentId.value === ALL_AGENTS_VALUE
    ? null
    : Number(selectedAgentId.value)
);
const inboxId = computed(() =>
  selectedInboxId.value === ALL_INBOXES_VALUE
    ? null
    : Number(selectedInboxId.value)
);
const selectedBoard = computed(() =>
  boards.value.find(board => String(board.id) === selectedBoardId.value)
);
const selectedAgent = computed(() =>
  agents.value.find(agent => String(agent.id) === selectedAgentId.value)
);

// Com "Todos os funis", as origens sao a uniao das origens dos funis; produtos sao de um funil so.
const { labelOptions, sourceOptions, productOptions } = useKanbanFilterOptions({
  boards: computed(() =>
    selectedBoard.value ? [selectedBoard.value] : boards.value
  ),
  products: computed(() => (boardId.value ? kanbanProducts.value : [])),
});
const optionLabel = (options, value) =>
  options.find(option => option.value === value)?.label || options[0].label;

const baseParams = computed(() => {
  const params = { since: since.value, until: until.value };
  if (boardId.value) params.board_id = boardId.value;
  if (agentId.value) params.agent_id = agentId.value;
  if (inboxId.value) params.inbox_id = inboxId.value;
  return { ...params, ...reportFilterParams.value };
});

const boardOptions = computed(() => [
  { label: t('KANBAN.REPORTS.ALL_BOARDS'), value: ALL_BOARDS_VALUE },
  ...boards.value.map(board => ({
    label: board.name,
    value: String(board.id),
  })),
]);
const selectedBoardLabel = computed(() =>
  selectedBoardId.value === ALL_BOARDS_VALUE
    ? t('KANBAN.REPORTS.ALL_BOARDS')
    : selectedBoard.value?.name || t('KANBAN.REPORTS.ALL_BOARDS')
);

const agentOptions = computed(() => [
  { label: t('KANBAN.REPORTS.ALL_AGENTS'), value: ALL_AGENTS_VALUE },
  ...agents.value.map(agent => ({
    label: agent.name,
    value: String(agent.id),
  })),
]);
const selectedAgentLabel = computed(() =>
  selectedAgentId.value === ALL_AGENTS_VALUE
    ? t('KANBAN.REPORTS.ALL_AGENTS')
    : selectedAgent.value?.name || t('KANBAN.REPORTS.ALL_AGENTS')
);

// Inbox options come from the inboxes assigned to funnels (already in the boards payload),
// so the list stays relevant to Kanban without an extra request.
const inboxesById = computed(() => {
  const map = new Map();
  boards.value.forEach(board =>
    (board.assigned_inboxes || []).forEach(inbox =>
      map.set(String(inbox.id), inbox)
    )
  );
  return map;
});
const inboxOptions = computed(() => [
  { label: t('KANBAN.REPORTS.ALL_INBOXES'), value: ALL_INBOXES_VALUE },
  ...[...inboxesById.value.values()].map(inbox => ({
    label: inbox.name,
    value: String(inbox.id),
  })),
]);
const selectedInboxLabel = computed(() =>
  selectedInboxId.value === ALL_INBOXES_VALUE
    ? t('KANBAN.REPORTS.ALL_INBOXES')
    : inboxesById.value.get(selectedInboxId.value)?.name ||
      t('KANBAN.REPORTS.ALL_INBOXES')
);

// Every funnel offers the two built-in axes; label axes come from its own settings, so a
// clinic sees "Especialidade" and a gym sees "Modalidade" without any code change.
const dimensionOptions = computed(() => [
  ...BUILT_IN_DIMENSIONS.map(key => ({
    label: t(`KANBAN.REPORTS.DIMENSION.BUILT_IN.${key.toUpperCase()}`),
    value: key,
  })),
  ...(selectedBoard.value?.settings?.dimensions || [])
    .filter(dimension => dimension.key || dimension.name)
    .map(dimension => ({
      label: dimension.name || dimension.key,
      value: String(dimension.key || dimension.name),
    })),
  ...buildAttributeDimensionOptions(attributesByModel.value, t),
]);

// Atributo escolhido como dimensao; so o de data mostra (e envia) o agrupamento por semana/mes/ano.
const selectedAttributeDefinition = computed(() => {
  const { dimension, attributeModel, attributeKey } = decodeDimension(
    selectedDimension.value
  );
  if (dimension !== CUSTOM_ATTRIBUTE_DIMENSION) return null;
  return attributesByModel
    .value(attributeModel)
    .find(definition => definition.attribute_key === attributeKey);
});
const isDateAttributeDimension = computed(
  () => selectedAttributeDefinition.value?.attribute_display_type === 'date'
);
const dimensionDateGroupOptions = computed(() =>
  DIMENSION_DATE_GROUPS.map(group => ({
    label: t(`KANBAN.REPORTS.DIMENSION.DATE_GROUP.${group.toUpperCase()}`),
    value: group,
  }))
);
const selectedDimensionDateGroupLabel = computed(
  () =>
    dimensionDateGroupOptions.value.find(
      option => option.value === dimensionDateGroup.value
    )?.label
);
const dimensionParams = computed(() =>
  dimensionRequestParams(
    selectedDimension.value,
    isDateAttributeDimension.value ? dimensionDateGroup.value : null
  )
);
const selectedDimensionLabel = computed(
  () =>
    dimensionOptions.value.find(
      option => option.value === selectedDimension.value
    )?.label || t('KANBAN.REPORTS.DIMENSION.BUILT_IN.SOURCE')
);

// A label axis belongs to one funnel; switching funnels (or deleting the axis) must not
// leave a key behind that this funnel can't resolve.
const normalizeDimension = () => {
  if (
    !dimensionOptions.value.some(
      option => option.value === selectedDimension.value
    )
  ) {
    selectedDimension.value = DEFAULT_DIMENSION;
  }
};

// Etiqueta apagada ou origem/produto que nao existem no funil escolhido voltam para "Todos".
const normalizeReportFilters = () => {
  [
    ['label', labelOptions],
    ['source', sourceOptions],
    ['productId', productOptions],
  ].forEach(([key, options]) => {
    if (!options.value.some(option => option.value === reportFilters[key])) {
      reportFilters[key] = 'all';
    }
  });
};

// Once the option lists are loaded, drop any board/agent/inbox id seeded from the URL that
// no longer exists (deleted or unauthorized), so a stale id can't leak into the API params.
const normalizeFilters = () => {
  if (
    selectedBoardId.value !== ALL_BOARDS_VALUE &&
    !boards.value.some(board => String(board.id) === selectedBoardId.value)
  ) {
    selectedBoardId.value = ALL_BOARDS_VALUE;
  }
  if (
    selectedAgentId.value !== ALL_AGENTS_VALUE &&
    !agents.value.some(agent => String(agent.id) === selectedAgentId.value)
  ) {
    selectedAgentId.value = ALL_AGENTS_VALUE;
  }
  if (
    selectedInboxId.value !== ALL_INBOXES_VALUE &&
    !inboxesById.value.has(selectedInboxId.value)
  ) {
    selectedInboxId.value = ALL_INBOXES_VALUE;
  }
  normalizeDimension();
  normalizeReportFilters();
};

const tabItems = computed(() =>
  REPORT_TAB_ORDER.map(key => ({
    key,
    label: t(`KANBAN.REPORTS.TABS.${key.toUpperCase()}`),
  }))
);
const activeTabIndex = computed(() =>
  REPORT_TAB_ORDER.indexOf(activeTab.value)
);
// O CFD le fotografias diarias do funil, sem agente, caixa nem dimensao: avisa quando algum filtro esta aplicado.
const cfdIgnoresFilters = computed(
  () =>
    Boolean(agentId.value || inboxId.value) ||
    Object.keys(reportFilterParams.value).length > 0
);
const requiresBoard = computed(() => activeTab.value !== REPORT_TABS.OVERVIEW);
const showBoardRequired = computed(() => requiresBoard.value && !boardId.value);

// CSV export options for the active tab (board-scoped tabs need a board first), plus the card
// list, which works for every tab and for all funnels at once.
const exportOptions = computed(() => [
  ...(showBoardRequired.value
    ? []
    : EXPORTABLE_REPORTS[activeTab.value] || []
  ).map(item => ({
    value: item.report,
    label: t(`KANBAN.REPORTS.${item.labelKey}`),
  })),
  ...(canExportTasks.value
    ? [
        {
          value: TASK_LIST_EXPORT,
          label: t('KANBAN.REPORTS.EXPORT.TASK_LIST.MENU'),
        },
      ]
    : []),
]);

const {
  reports,
  fetchSummary,
  fetchTimeseries,
  fetchAgentPerformance,
  fetchFunnel,
  fetchCfd,
  fetchAging,
  fetchStageDurations,
  fetchDimension,
} = useKanbanReports();

const reloadSummary = () => fetchSummary(baseParams.value);
const reloadTimeseries = () =>
  fetchTimeseries({
    ...baseParams.value,
    metric: metric.value,
    group_by: groupBy.value,
  });
const reloadAgentPerformance = () => fetchAgentPerformance(baseParams.value);
const reloadFunnel = () => fetchFunnel(baseParams.value);
const reloadCfd = () =>
  fetchCfd({ ...baseParams.value, group_by: cfdGroupBy.value });
const reloadAging = () =>
  fetchAging({ ...baseParams.value, page: agingPage.value });
const reloadStageDurations = () => fetchStageDurations(baseParams.value);
const reloadDimension = () =>
  fetchDimension({ ...baseParams.value, ...dimensionParams.value });

// Lazy-load: only fetch what the active tab needs. No-op behind the paywall so we never
// fire report requests the user can't see.
const loadActiveTab = () => {
  if (!isKanbanEnabled.value) return;
  if (activeTab.value === REPORT_TABS.OVERVIEW) {
    reloadSummary();
    reloadTimeseries();
    reloadAgentPerformance();
  } else if (activeTab.value === REPORT_TABS.FUNNEL && boardId.value) {
    reloadFunnel();
  } else if (activeTab.value === REPORT_TABS.DIMENSION && boardId.value) {
    reloadDimension();
  } else if (activeTab.value === REPORT_TABS.FLOW && boardId.value) {
    reloadCfd();
    reloadAging();
    reloadStageDurations();
  }
};

// Produto e origem sao do funil: trocar de funil zera o produto e solta a origem que nao existe no novo, antes
// do recarregamento, para nenhuma requisicao sair com o filtro do funil anterior.
const onBoardSelect = value => {
  selectedBoardId.value = value;
  reportFilters.productId = 'all';
  normalizeReportFilters();
};
const onAgentSelect = value => {
  selectedAgentId.value = value;
};
const onInboxSelect = value => {
  selectedInboxId.value = value;
};
const onTabChanged = tab => {
  activeTab.value = tab.key;
};
const onDateRangeChanged = ([start, end, type]) => {
  dateRange.value = [start, end];
  rangeType.value = type;
};
const onMetricChange = value => {
  metric.value = value;
  reloadTimeseries();
};
const onGroupByChange = value => {
  groupBy.value = value;
  reloadTimeseries();
};
const onCfdGroupByChange = value => {
  cfdGroupBy.value = value;
  reloadCfd();
};
const onDimensionSelect = value => {
  selectedDimension.value = value;
  reloadDimension();
};
const onDimensionDateGroupSelect = value => {
  dimensionDateGroup.value = value;
  reloadDimension();
};
const onAgingPage = page => {
  agingPage.value = page;
  reloadAging();
};

// Extra params per report so the CSV matches what's on screen (granularity, page).
const downloadParams = report => {
  const params = { ...baseParams.value };
  if (report === 'timeseries') {
    params.metric = metric.value;
    params.group_by = groupBy.value;
  } else if (report === 'cfd') {
    params.group_by = cfdGroupBy.value;
  } else if (report === 'aging') {
    params.page = agingPage.value;
  } else if (report === 'dimension') {
    Object.assign(params, dimensionParams.value);
  }
  return params;
};
const taskExportDialog = ref(null);
const isExportingTasks = ref(false);

const onExport = async report => {
  if (report === TASK_LIST_EXPORT) {
    taskExportDialog.value?.open();
    return;
  }
  try {
    const csv = await ReportsAPI.download(report, downloadParams(report));
    downloadBlob(
      `kanban-${report}-${format(new Date(), 'dd-MM-yyyy')}.csv`,
      csv
    );
  } catch {
    useAlert(t('KANBAN.REPORTS.EXPORT.ERROR'));
  }
};

// 200 traz o arquivo; 202 avisa que ele vai por e-mail (lista grande); erro traz a mensagem do servidor em JSON.
const onExportTasks = async ({ fileFormat, createdInPeriod }) => {
  isExportingTasks.value = true;
  try {
    const response = await ReportsAPI.exportTasks({
      ...baseParams.value,
      file_format: fileFormat,
      created_in_period: createdInPeriod,
    });
    if (response.status === 202) {
      const { email } = (await readJsonBlob(response.data)) || {};
      useAlert(t('KANBAN.REPORTS.EXPORT.TASK_LIST.QUEUED', { email }));
    } else {
      downloadBlob(
        fileNameFromDisposition(response.headers['content-disposition']) ||
          `cartoes.${fileFormat}`,
        response.data
      );
    }
    taskExportDialog.value?.close();
  } catch (error) {
    const body =
      error.response?.data && (await readJsonBlob(error.response.data));
    useAlert(body?.error || t('KANBAN.REPORTS.EXPORT.ERROR'));
  } finally {
    isExportingTasks.value = false;
  }
};

watch(
  [
    boardId,
    agentId,
    inboxId,
    since,
    until,
    () => JSON.stringify(reportFilterParams.value),
  ],
  () => {
    agingPage.value = 1;
    normalizeDimension();
    loadActiveTab();
  }
);

const loadBoardProducts = () =>
  boardId.value
    ? store.dispatch('kanban/fetchProducts', { boardId: boardId.value })
    : Promise.resolve();
watch(boardId, loadBoardProducts);
watch(activeTab, () => loadActiveTab());

// Keep board/agent/inbox/tab/period in the URL so the report is shareable and survives refresh.
watch(
  [
    selectedBoardId,
    selectedAgentId,
    selectedInboxId,
    activeTab,
    rangeType,
    since,
    until,
    selectedDimension,
    dimensionDateGroup,
    reportFilterParams,
  ],
  () => {
    const query = {
      tab: activeTab.value,
      range_type: rangeType.value,
      since: String(since.value),
      until: String(until.value),
    };
    if (selectedBoardId.value !== ALL_BOARDS_VALUE) {
      query.board_id = selectedBoardId.value;
    }
    if (selectedAgentId.value !== ALL_AGENTS_VALUE) {
      query.agent_id = selectedAgentId.value;
    }
    if (selectedInboxId.value !== ALL_INBOXES_VALUE) {
      query.inbox_id = selectedInboxId.value;
    }
    if (activeTab.value === REPORT_TABS.DIMENSION) {
      Object.assign(query, dimensionParams.value);
    }
    Object.assign(query, reportFilterParams.value);
    router.replace({ query });
  }
);

// Skip board/agent/report fetches behind the paywall; re-run once access is granted (the
// subscription state can resolve after mount).
const initialize = async () => {
  if (!isKanbanEnabled.value) return;
  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('kanban/fetchBoards'),
    store.dispatch('attributes/get'),
    store.dispatch('labels/get'),
    loadBoardProducts(),
  ]);
  const { filters: valid, dropped } = filtersFromRouteQuery(
    route.query,
    taskAttributeDefinitions.value
  );
  reportFilters.attributeFilters = valid.attributeFilters;
  if (dropped) useAlert(t('KANBAN.FILTERS.INVALID_FILTERS_DROPPED'));
  normalizeFilters();
  loadActiveTab();
};
watch(isKanbanEnabled, enabled => {
  if (enabled) initialize();
});
onMounted(initialize);
</script>

<template>
  <div class="font-inter">
    <div
      v-if="!isKanbanEnabled"
      class="flex items-center justify-center min-h-[400px] py-12"
    >
      <BasePaywallModal
        feature-prefix="KANBAN"
        :i18n-key="paywallI18nKey"
        :is-super-admin="isSuperAdmin"
        :is-on-chatwoot-cloud="isOnChatwootCloud"
        super-admin-url="/super_admin/settings"
        @upgrade="openBilling"
      />
    </div>

    <div v-else>
      <ReportHeader :header-title="t('KANBAN.REPORTS.TITLE')">
        <SelectMenu
          v-if="exportOptions.length"
          model-value=""
          :options="exportOptions"
          :label="t('KANBAN.REPORTS.EXPORT.LABEL')"
          icon="i-lucide-download"
          sub-menu-position="bottom"
          max-width="max-w-56"
          @update:model-value="onExport"
        />
      </ReportHeader>

      <div class="flex flex-col gap-6 pb-6">
        <div
          class="flex flex-col w-full gap-3 lg:flex-row lg:flex-wrap lg:items-center"
        >
          <WootDatePicker
            v-model:date-range="dateRange"
            v-model:range-type="rangeType"
            @date-range-changed="onDateRangeChanged"
          />
          <div
            class="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-end lg:ms-auto"
          >
            <SelectMenu
              class="shrink-0"
              :model-value="selectedBoardId"
              :options="boardOptions"
              :label="selectedBoardLabel"
              icon="i-lucide-kanban"
              show-avatar
              sub-menu-position="bottom"
              max-width="max-w-56"
              @update:model-value="onBoardSelect"
            />
            <SelectMenu
              class="shrink-0"
              :model-value="selectedAgentId"
              :options="agentOptions"
              :label="selectedAgentLabel"
              icon="i-lucide-user"
              show-avatar
              sub-menu-position="bottom"
              max-width="max-w-56"
              @update:model-value="onAgentSelect"
            />
            <SelectMenu
              v-if="inboxOptions.length > 1"
              class="shrink-0"
              :model-value="selectedInboxId"
              :options="inboxOptions"
              :label="selectedInboxLabel"
              icon="i-lucide-inbox"
              sub-menu-position="bottom"
              max-width="max-w-56"
              @update:model-value="onInboxSelect"
            />
            <SelectMenu
              v-if="labelOptions.length > 1"
              class="shrink-0"
              :model-value="reportFilters.label"
              :options="labelOptions"
              :label="optionLabel(labelOptions, reportFilters.label)"
              icon="i-lucide-tag"
              sub-menu-position="bottom"
              max-width="max-w-56"
              @update:model-value="reportFilters.label = $event"
            />
            <SelectMenu
              v-if="sourceOptions.length > 1"
              class="shrink-0"
              :model-value="reportFilters.source"
              :options="sourceOptions"
              :label="optionLabel(sourceOptions, reportFilters.source)"
              icon="i-lucide-radio"
              sub-menu-position="bottom"
              max-width="max-w-56"
              @update:model-value="reportFilters.source = $event"
            />
            <SelectMenu
              v-if="productOptions.length > 1"
              class="shrink-0"
              :model-value="reportFilters.productId"
              :options="productOptions"
              :label="optionLabel(productOptions, reportFilters.productId)"
              icon="i-lucide-package"
              sub-menu-position="bottom"
              max-width="max-w-56"
              @update:model-value="reportFilters.productId = $event"
            />
            <div class="relative">
              <OnClickOutside @trigger="showAttributeFilters = false">
                <Button
                  v-tooltip="t('KANBAN.FILTERS.ATTRIBUTES.BUTTON')"
                  icon="i-lucide-filter"
                  variant="ghost"
                  :color="
                    reportFilters.attributeFilters.length ? 'blue' : 'slate'
                  "
                  size="sm"
                  :label="
                    reportFilters.attributeFilters.length
                      ? String(reportFilters.attributeFilters.length)
                      : undefined
                  "
                  @click="showAttributeFilters = !showAttributeFilters"
                />
                <TaskAttributeFiltersPanel
                  v-if="showAttributeFilters"
                  v-model="reportFilters.attributeFilters"
                  class="absolute end-0 z-50 top-10"
                  :definitions="taskAttributeDefinitions"
                  @close="showAttributeFilters = false"
                />
              </OnClickOutside>
            </div>
            <SelectMenu
              v-if="activeTab === REPORT_TABS.DIMENSION"
              class="shrink-0"
              :model-value="selectedDimension"
              :options="dimensionOptions"
              :label="selectedDimensionLabel"
              icon="i-lucide-layers"
              sub-menu-position="bottom"
              max-width="max-w-56"
              @update:model-value="onDimensionSelect"
            />
            <SelectMenu
              v-if="
                activeTab === REPORT_TABS.DIMENSION && isDateAttributeDimension
              "
              class="shrink-0"
              :model-value="dimensionDateGroup"
              :options="dimensionDateGroupOptions"
              :label="selectedDimensionDateGroupLabel"
              icon="i-lucide-calendar"
              sub-menu-position="bottom"
              max-width="max-w-56"
              @update:model-value="onDimensionDateGroupSelect"
            />
          </div>
        </div>

        <TabBar
          :tabs="tabItems"
          :initial-active-tab="activeTabIndex"
          @tab-changed="onTabChanged"
        />

        <div class="flex flex-col gap-6">
          <template v-if="activeTab === REPORT_TABS.OVERVIEW">
            <KanbanSummaryCards
              :data="reports.summary.data"
              :is-loading="reports.summary.isLoading"
              :error="reports.summary.error"
              :locale="locale"
              @retry="reloadSummary"
            />
            <KanbanThroughputChart
              :data="reports.timeseries.data"
              :is-loading="reports.timeseries.isLoading"
              :error="reports.timeseries.error"
              :metric="metric"
              :group-by="groupBy"
              :locale="locale"
              @retry="reloadTimeseries"
              @update:metric="onMetricChange"
              @update:group-by="onGroupByChange"
            />
            <KanbanAgentLeaderboard
              :data="reports.agentPerformance.data"
              :is-loading="reports.agentPerformance.isLoading"
              :error="reports.agentPerformance.error"
              :locale="locale"
              @retry="reloadAgentPerformance"
            />
          </template>

          <template v-else-if="activeTab === REPORT_TABS.DIMENSION">
            <KanbanBoardRequiredState
              v-if="showBoardRequired"
              :boards="boards"
              @select="onBoardSelect(String($event))"
            />
            <template v-else>
              <KanbanDimensionCharts
                :data="reports.dimension.data"
                :is-loading="reports.dimension.isLoading"
                :error="reports.dimension.error"
                :locale="locale"
                @retry="reloadDimension"
              />
              <KanbanDimensionTable
                :data="reports.dimension.data"
                :is-loading="reports.dimension.isLoading"
                :error="reports.dimension.error"
                :locale="locale"
                @retry="reloadDimension"
              />
            </template>
          </template>

          <template v-else-if="activeTab === REPORT_TABS.FUNNEL">
            <KanbanBoardRequiredState
              v-if="showBoardRequired"
              :boards="boards"
              @select="onBoardSelect(String($event))"
            />
            <KanbanFunnelChart
              v-else
              :data="reports.funnel.data"
              :is-loading="reports.funnel.isLoading"
              :error="reports.funnel.error"
              :locale="locale"
              @retry="reloadFunnel"
            />
          </template>

          <template v-else-if="activeTab === REPORT_TABS.FLOW">
            <KanbanBoardRequiredState
              v-if="showBoardRequired"
              :boards="boards"
              @select="onBoardSelect(String($event))"
            />
            <template v-else>
              <p v-if="cfdIgnoresFilters" class="text-xs text-n-slate-11">
                {{ t('KANBAN.REPORTS.FILTERS.CFD_IGNORES_FILTERS') }}
              </p>
              <KanbanCfdChart
                :data="reports.cfd.data"
                :is-loading="reports.cfd.isLoading"
                :error="reports.cfd.error"
                :group-by="cfdGroupBy"
                :locale="locale"
                @retry="reloadCfd"
                @update:group-by="onCfdGroupByChange"
              />
              <KanbanAgingTable
                :data="reports.aging.data"
                :is-loading="reports.aging.isLoading"
                :error="reports.aging.error"
                :locale="locale"
                @retry="reloadAging"
                @update:page="onAgingPage"
              />
              <KanbanStageDurations
                :data="reports.stageDurations.data || []"
                :is-loading="reports.stageDurations.isLoading"
                :error="reports.stageDurations.error"
                :locale="locale"
                @retry="reloadStageDurations"
              />
            </template>
          </template>
        </div>
      </div>
    </div>
    <KanbanTaskExportDialog
      ref="taskExportDialog"
      :is-exporting="isExportingTasks"
      @export="onExportTasks"
    />
  </div>
</template>
